import { useState } from 'react'
import { useNavigate } from 'react-router'
import {
  getDocumentsUrlWithCurrentParams,
  useCurrentDocument,
  useUpdateDocumentOnshoreOnly,
} from '~/entities/document'
import { useUserStore } from '~/entities/user'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { Switch, Text, toast } from '~/shared/ui'
import { OnshoreOnlyDialog } from './onshore-only-dialog'

export function ToggleOnshoreOnly() {
  const isEnabled = useIsFeatureEnabled('2026-06-manual-onshore-toggle')
  const isOnshore = useUserStore((state) => state.isOnshore)
  const navigate = useNavigate()
  const {
    data: { document },
  } = useCurrentDocument()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const mutation = useUpdateDocumentOnshoreOnly()
  const [error, setError] = useState<string | null>(null)

  if (!isEnabled) {
    return null
  }

  const isCurrentlyOnshoreOnly = !!document.onshore_only
  // Offshore users can only move a document to onshore-only (a one-way transition
  // that removes their own visibility). They cannot revert an onshore-only document
  // back to offshore-accessible — the API rejects that with a 403.
  const canRevert = isOnshore
  const isSwitchDisabled = !canRevert && isCurrentlyOnshoreOnly

  const handleSwitchClick = () => {
    if (isSwitchDisabled) {
      return
    }
    setError(null)
    setIsDialogOpen(true)
  }

  const handleConfirm = (notes: string) => {
    const nextOnshoreOnly = !isCurrentlyOnshoreOnly
    mutation.mutate(
      { key: document.key, onshoreOnly: nextOnshoreOnly, notes },
      {
        onSuccess: () => {
          setIsDialogOpen(false)
          toast('Document visibility updated')
          // Offshore users lose visibility over a document once it becomes
          // onshore-only, so send them back to the documents list rather than
          // leaving them on a page they can no longer access.
          if (!isOnshore && nextOnshoreOnly) {
            navigate(getDocumentsUrlWithCurrentParams())
          }
        },
        onError: (err) => {
          const status = (err as { status?: number }).status
          if (status === 409) {
            setError('This document already has this visibility setting.')
          } else if (status === 403) {
            setError('You do not have permission to change document visibility.')
          } else {
            setError('Something went wrong. Please try again.')
          }
        },
      },
    )
  }

  return (
    <>
      <div className="flex items-center gap-1.5">
        <Text
          className="text-text-secondary"
          appearance="CAPTION"
        >
          US Only
        </Text>
        <Switch
          checked={isCurrentlyOnshoreOnly}
          disabled={isSwitchDisabled}
          onClick={handleSwitchClick}
        />
      </div>
      {isDialogOpen && (
        <OnshoreOnlyDialog
          isSettingOnshore={!isCurrentlyOnshoreOnly}
          isOffshoreUser={!isOnshore}
          isPending={mutation.isPending}
          error={error}
          onClose={() => setIsDialogOpen(false)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  )
}
